import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import {
  type UtilizationReportGroupedByQuery,
  type UtilizationReportQuery,
  useUtilizationReportExportQuery,
} from "@src/__generated__/urql-graphql";
import { UtilizationReportStore } from "@src/components/modules/reports/utilization/stores/utilization-report";
import { UtilizationUserReportModal } from "@src/components/modules/reports/utilization/UtilizationUserReportModal";
import {
  Avatar,
  DataTable,
  DropdownButton,
  ExportButton,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { PeriodPicker } from "@src/components/widgets/PeriodPicker";
import { FiltersPanelRoot } from "@src/utils/components/filters/components/filters-panel";
import { FiltersPanelFilters } from "@src/utils/components/filters/components/filters-panel-filters";
import { can } from "@src/utils/components/permissions";
import { useStore } from "@src/utils/hooks";
import { formatSecondsToHours } from "@src/utils/time";
import { differenceInMonths } from "date-fns";
import { computed } from "mobx";
import { observer, useLocalObservable } from "mobx-react-lite";
import { now } from "mobx-utils";
import { type ReactNode, useEffect } from "react";
import { GroupByMenu } from "./components/group-by-menu";
import { useUtilizationFiltersFromQuery } from "./hooks/filtersFromQuery";
import { useUtilizationOptions } from "./hooks/options";
import { BillableUtilizationStatCard } from "./stat-cards/BillableUtilization";
import { TotalCapacityStatCard } from "./stat-cards/TotalCapacity";
import { TrackedAllocatedStatCard } from "./stat-cards/TrackedAllocated";
import { UtilizationStatCard } from "./stat-cards/Utilization";
import { GroupByTable } from "./tables/GroupByTable";
import { Table } from "./tables/Table";

type UtilizationReportRow =
  UtilizationReportQuery["utilizationReport"]["data"][0];
type UtilizationReportRowGrouped =
  UtilizationReportGroupedByQuery["utilizationReportGroups"]["data"][0];

export const userCell = (
  user: UtilizationReportRow["user"],
  onClick: () => void,
) => {
  return (
    <HStack spacing="2">
      <Avatar
        name={user.full_name}
        src={user.image?.urls.thumbnail}
        colorScheme={user.profile?.hex_color}
      />
      <Button
        fontWeight="medium"
        onClick={onClick}
        role="link"
        size="xs"
        variant="link"
      >
        {user.full_name}
      </Button>
    </HStack>
  );
};

export const AvailableCapacityTooltip = ({
  children,
  row,
}: {
  children: ReactNode;
  row: UtilizationReportRow | UtilizationReportRowGrouped;
}) => (
  <Tooltip
    p="5"
    label={
      <HStack fontSize="xs" spacing="5">
        <Box textAlign="start">
          <span>
            <Trans>Capacity</Trans>
          </span>
          <Text mb="0.5" color="teal.400" fontSize="sm">
            {formatSecondsToHours(row.report.available_time, 1)}
          </Text>
        </Box>

        <Box alignItems="start" textAlign="start">
          <span>
            <Trans>Time-offs</Trans>
          </span>
          <Text mb="0.5" fontSize="sm">
            {formatSecondsToHours(row.report.time_off, 1)}
          </Text>
        </Box>
      </HStack>
    }
    rounded="lg"
  >
    {children}
  </Tooltip>
);

type TrackedAndAllocatedTooltipProps = {
  children: ReactNode;
  row: UtilizationReportRow | UtilizationReportRowGrouped;
  date: Date;
};

export const TrackedAndAllocatedTooltip = ({
  children,
  row,
  date,
}: TrackedAndAllocatedTooltipProps) => {
  const isDateInPast = computed(() => {
    return differenceInMonths(date, now()) < 0;
  }).get();

  return (
    <Tooltip
      p="5"
      label={
        <HStack w="fit-content" minW="fit-content" fontSize="xs" spacing="5">
          <Box textAlign="start">
            <span>
              <Trans>Tracked</Trans>
            </span>
            <HStack>
              <Icon name="clock" color="teal.400" />
              <Text mb="0.5" color="teal.400" fontSize="sm">
                {formatSecondsToHours(row.report.tracked_time ?? 0, 1)}
              </Text>
            </HStack>
          </Box>

          <Box alignItems="start" textAlign="start">
            <span>
              <Trans>Future Allocated</Trans>
            </span>
            <Text mb="0.5" color="#C4A1FF" fontSize="sm">
              {formatSecondsToHours(row.report.allocated_time ?? 0, 1)}
            </Text>
          </Box>
          <Box alignItems="start" textAlign="start">
            <span>
              {isDateInPast ? (
                <Trans>Unused hours</Trans>
              ) : (
                <Trans>Left to Allocate</Trans>
              )}
            </span>
            <Text mb="0.5" color="#C4A1FF" fontSize="sm">
              {formatSecondsToHours(row.report.remaining_time, 1)}
            </Text>
          </Box>
        </HStack>
      }
      rounded="lg"
    >
      {children}
    </Tooltip>
  );
};

export const UtilizationReport = observer(function UtilizationReport() {
  const appStore = useStore();
  const { store } = useLocalObservable(() => ({
    store: new UtilizationReportStore(appStore),
  }));

  useUtilizationFiltersFromQuery(store);
  useUtilizationOptions(store);

  const [{ data, fetching: exportingReport }, exportUtilizationReport] =
    useUtilizationReportExportQuery({
      pause: true,
      variables: {
        where: store.where.asWhereParam,
        to: store.dateRange.end,
        from: store.dateRange.start,
        dataSource: store.dataSource,
      },
    });

  useEffect(() => {
    if (!data?.utilizationReportExport) return;

    window.open(data.utilizationReportExport, "_blank");
  }, [data?.utilizationReportExport]);

  return (
    <DataTable>
      <DataTable.NavBar>
        <Box py="4">
          <FiltersPanelRoot filters={store.where}>
            <Stack>
              <HStack justifyContent="space-between">
                <HStack spacing="2">
                  <PeriodPicker
                    value={store.dateRange}
                    onChange={store.handleChangeDateRange}
                  />
                  <GroupByMenu
                    activeGroupByOption={store.activeGroupByOption}
                    groupByOptions={store.groupByOptions}
                    onGroupByChange={store.handleChangeGroupBy}
                    onResetPage={store.pagination.resetPage}
                  />

                  <FiltersPanelFilters
                    innerMenuPlacement="right-end"
                    clearButtonSide="right"
                  />
                </HStack>

                <HStack spacing="2">
                  <Menu>
                    <MenuButton as={DropdownButton}>
                      <Trans>Data source</Trans>
                      <span>:</span>
                      <Text as="span" ml="1" color="blue.500">
                        {store.activeDataSourceOption?.label}
                      </Text>
                    </MenuButton>
                    <MenuList>
                      {store.dataSourceOptions.map(({ value, label }) => (
                        <MenuItem
                          key={value}
                          onClick={() => {
                            store.handleChangeDataSource(value);
                          }}
                        >
                          {label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </Menu>
                  {can("utilizationReport_query_all") && (
                    <ExportButton
                      onClick={() => {
                        exportUtilizationReport();
                      }}
                      isLoading={exportingReport}
                    />
                  )}
                </HStack>
              </HStack>
            </Stack>
          </FiltersPanelRoot>
        </Box>
      </DataTable.NavBar>

      <DataTable.Stats>
        <TotalCapacityStatCard
          totalTime={store.tableStatsData?.total_time}
          availableTime={store.tableStatsData?.available_time}
          timeOff={store.tableStatsData?.time_off}
        />
        <TrackedAllocatedStatCard
          allocatedTime={store.tableStatsData?.allocated_time ?? 0}
          trackedTime={store.tableStatsData?.tracked_time ?? 0}
          taskBudgetedTime={store.tableStatsData?.budgeted_time ?? 0}
          totalTime={store.tableStatsData?.available_time}
          combinedTime={store.tableStatsData?.combined_time ?? 0}
          activeDataSource={store.activeDataSourceOption?.value}
        />
        <UtilizationStatCard
          percent={store.tableStatsData?.utilization_percentage ?? 0}
          totalTime={store.tableStatsData?.available_time ?? 0}
          combinedTime={store.tableStatsData?.combined_time ?? 0}
          allocatedTime={store.tableStatsData?.allocated_time ?? 0}
          budgetedTime={store.tableStatsData?.budgeted_time ?? 0}
          trackedTime={store.tableStatsData?.tracked_time ?? 0}
          month={store.dateRange.start}
          activeDataSource={store.activeDataSourceOption?.value}
          utilizationRange={{
            from:
              appStore.workspaceStore?.settings
                ?.default_utilization_range_from ?? 80,
            to:
              appStore.workspaceStore?.settings?.default_utilization_range_to ??
              100,
          }}
        />
        <BillableUtilizationStatCard
          percent={store.tableStatsData?.utilization_percentage_billable ?? 0}
          totalTime={store.tableStatsData?.available_time ?? 0}
          combinedTime={store.tableStatsData?.combined_time ?? 0}
          allocatedTime={store.tableStatsData?.allocated_time ?? 0}
          budgetedTime={store.tableStatsData?.budgeted_time ?? 0}
          trackedTime={store.tableStatsData?.tracked_time ?? 0}
          timeOffTime={store.tableStatsData?.time_off ?? 0}
          combinedTimeBillable={
            store.tableStatsData?.combined_time_billable ?? 0
          }
          allocatedTimeBillable={
            store.tableStatsData?.allocated_time_billable ?? 0
          }
          budgetedTimeBillable={
            store.tableStatsData?.budgeted_time_billable ?? 0
          }
          trackedTimeBillable={store.tableStatsData?.tracked_time_billable ?? 0}
          activeDataSource={store.activeDataSourceOption?.value}
          utilizationRange={{
            from:
              appStore.workspaceStore?.settings
                ?.default_utilization_range_from ?? 80,
            to:
              appStore.workspaceStore?.settings?.default_utilization_range_to ??
              100,
          }}
        />
      </DataTable.Stats>

      {store.groupBy ? <GroupByTable store={store} /> : <Table store={store} />}

      <UtilizationUserReportModal
        store={store.userModalStore}
        activeDataSource={store.activeDataSourceOption?.value}
      />
    </DataTable>
  );
});
